import type { DentistDetails, DraftNote } from '@/types';

/**
 * What the review page keeps in the dentist's own browser: their details, so
 * they are typed once rather than on every case, and the notes they have
 * added but not sent yet, so a reload does not lose them. Nothing here goes
 * to the server until the dentist sends their decision.
 *
 * Storage can be missing or refuse (a private window, blocked site data): every
 * read and write is guarded, and the page works the same without it.
 */

const DENTIST_KEY = 'nudent.review.dentist';
const DRAFTS_PREFIX = 'nudent.review.drafts.';

function read(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage refused: the details are simply asked for again next time.
  }
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The details the dentist gave last time on this browser, or null. */
export function rememberedDentist(): (DentistDetails & { email: string }) | null {
  const stored = read(DENTIST_KEY);
  if (typeof stored !== 'object' || stored === null) return null;
  const item = stored as Record<string, unknown>;
  const name = text(item.name).trim();
  if (!name) return null;
  return {
    name,
    clinic: text(item.clinic),
    registrationNo: text(item.registrationNo),
    email: text(item.email),
  };
}

export function rememberDentist(details: DentistDetails & { email?: string }): void {
  write(DENTIST_KEY, {
    name: details.name.trim(),
    clinic: details.clinic.trim(),
    registrationNo: details.registrationNo.trim(),
    email: (details.email ?? '').trim(),
  });
}

const STAGES = new Set(['approve', 'changes_requested', 'reject']);

/** The unsent notes for one link. */
export function storedDrafts(key: string): DraftNote[] {
  const stored = read(DRAFTS_PREFIX + key);
  if (!Array.isArray(stored)) return [];
  return stored.flatMap((entry: unknown): DraftNote[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const item = entry as Record<string, unknown>;
    const note = text(item.text).trim();
    if (!note || typeof item.id !== 'string') return [];
    const position =
      Array.isArray(item.position) &&
      item.position.length === 3 &&
      item.position.every((v: unknown) => typeof v === 'number' && Number.isFinite(v))
        ? (item.position as [number, number, number])
        : null;
    return [
      {
        id: item.id,
        text: note,
        stage:
          typeof item.stage === 'string' && STAGES.has(item.stage)
            ? (item.stage as DraftNote['stage'])
            : null,
        position,
        view: (item.view ?? null) as DraftNote['view'],
        createdAt: text(item.createdAt) || new Date().toISOString(),
      },
    ];
  });
}

export function storeDrafts(key: string, drafts: DraftNote[]): void {
  write(DRAFTS_PREFIX + key, drafts.length ? drafts : null);
}

const PANEL_PREFIX = 'nudent.review.panel.';

/** Where the dentist last moved a stage panel (the Cut view), or null for its usual spot. */
export function rememberedPanelPosition(name: string): { x: number; y: number } | null {
  const stored = read(PANEL_PREFIX + name);
  if (typeof stored !== 'object' || stored === null) return null;
  const { x, y } = stored as Record<string, unknown>;
  return typeof x === 'number' && typeof y === 'number' ? { x, y } : null;
}

export function rememberPanelPosition(
  name: string,
  position: { x: number; y: number } | null,
): void {
  write(PANEL_PREFIX + name, position);
}
